import { useEffect, useState, useRef } from 'react';
import moment from 'moment';
import { CALLBACK_DELAY } from 'home/view/upgrade-order/constants';
import Environment from 'b2b-ui-components-lib/lib/services/environment';

import { findParamFromUrl, removeFromSessionStorage } from 'common/utils/app.utils';

import { PRODUCT_NAME, SESSION_STORAGE, DOCUMENT_TYPE_ENUM, FIELD_ERROR, MEDIA_TYPES, LEAD_STATUS } from 'home/view/upgrade-order/constants';

const formElement = { value: null, errors: [], touched: false };

const doValidationByRegex = (regex, value, error) => {
  const errors = [];
  if (value === null || value === undefined || value.length === 0) {
    errors.push('This is a mandatory field');
  } else if (regex && !regex.test(value)) {
    errors.push(error);
  }
  return errors;
};

const numberValidator = (value) => {
  const MAX_ALLOWED_INT = 2147483647;

  if (value && parseInt(value) > MAX_ALLOWED_INT) {
    return ['Please enter a valid value'];
  }

  return doValidationByRegex(/^(\s*|\d+)$/, value, 'Please enter only numeric characters');
};

const alphaNumbericValidator = (value) => doValidationByRegex(/^[0-9a-zA-Z]+$/, value, 'Please enter only alphanumeric characters');

const alphaNumericWithSpecialCharactersValidator = (value) => doValidationByRegex(/^[ A-Za-z0-9_@./\\:#&+-]*$/, value, 'Please enter valid value');

const mobileValidator = (value) => doValidationByRegex(/^[6-9]{1}[0-9]{9}$/, value, FIELD_ERROR.INVALID_NO);

const emailValidator = (email) => doValidationByRegex(/^[_A-Za-z0-9-\+]+(\.[_A-Za-z0-9-]+)*@[\w-]+(\.[\w-]+)*(\.[\w-]{2,})$/, email, 'Please enter valid email');
const getDate = (dateString) => {
  const dateMomentObject = moment(dateString, 'YYYY-MM-DD');
  return dateMomentObject.toDate();
};

const sortedKeyList = (arr, documentType) => {
  let sortedArray = [];

  switch (documentType) {
    case DOCUMENT_TYPE_ENUM.PURCHASE_ORDER: {
      sortedArray = arr.sort((a, b) => {
        const firstItemIndex = a.split('').reverse().join('').split('_')[0] || '';
        const modifiedFirstIndex = firstItemIndex.length === 1 ? parseInt(firstItemIndex) : 0;
        const secondItemIndex = b.split('').reverse().join('').split('_')[0] || '';
        const modifiedSecondIndex = secondItemIndex.length === 1 ? parseInt(secondItemIndex) : 0;

        return modifiedFirstIndex - modifiedSecondIndex;
      });

      break;
    }

    default: {
      sortedArray = [...arr];
    }
  }

  return sortedArray;
};

const validateFileAndGetFormData = ({ event, supportedTypes, maxFileLimit, documentNames, documentType, allowMultiplePDFs = false, files = [], isDropEvent = false }) => {
  //maxFileLimit is in kb
  event.stopPropagation();
  let isFilesValid = true;
  let errorMessage = '';
  const filesMetaData = [];
  // const { files: selectedFiles } = event.target;
  let selectedFiles = event?.target?.files;

  if (isDropEvent) {
    selectedFiles = event.dataTransfer.files;
  }
  const formData = new FormData();
  const fileList = Array.from(selectedFiles);

  const schemaKeyList = documentNames;

  let activeKeyList = [];
  const uploadedKeyList = [];

  files.forEach((item) => {
    uploadedKeyList.push(item.documentName);
  });

  const filteredActiveKeyList = schemaKeyList.filter((item) => !uploadedKeyList.includes(item));

  if (filteredActiveKeyList.length > 0) {
    activeKeyList = sortedKeyList(filteredActiveKeyList, documentType);
  }

  fileList.forEach((file, index) => {
    const { type, size, name } = file;

    if (fileList.length > activeKeyList.length) {
      errorMessage = `Maximum ${documentNames.length} files are allowed in multiple upload`;
      isFilesValid = false;
    }

    if (!(Array.isArray(supportedTypes) && supportedTypes.includes(type))) {
      errorMessage = `Invalid file format. Try to re-upload the correct format`;
      isFilesValid = false;
    } else if (!allowMultiplePDFs && type === 'application/pdf' && fileList.length > 1) {
      errorMessage = 'Only single upload is allowed in case of pdf file';
      isFilesValid = false;
    } else if (!(size <= maxFileLimit)) {
      errorMessage = `Maximum file size is ${maxFileLimit / 1024 / 1024} Mb`;
      isFilesValid = false;
    }
    formData.append(`documents['${activeKeyList[index]}']`, file);
    filesMetaData.push({ name, size, type, documentName: activeKeyList[index] });
  });
  formData.append('documentType', documentType);

  return { isFilesValid, formData, filesMetaData, errorMessage };
};

const isFormValid = (form, keyToIgnore) => {
  if (typeof form === 'object' && form !== null) {
    return Object.entries(form).every(([key, value]) => {
      if (key === 'errors' && Array.isArray(value) && value.length > 0) {
        return false;
      } else if (Array.isArray(keyToIgnore) && (keyToIgnore.includes(key) || key === 'touched')) {
        return true;
      } else if (typeof value === 'object' && value !== null) {
        return isFormValid(form[key], keyToIgnore);
      }
      return !!value;
    });
  }
};

const getFileName = (res, defaultFileName) => {
  const filename = res && res.headers && res.headers.hasOwnProperty('content-disposition') && res.headers['content-disposition'].split('filename=')[1];

  return filename || defaultFileName;
};

const getFileType = (type) => {
  if (type === 'pdf') {
    return { type: 'application/pdf' };
  }

  if (type === 'jpeg') {
    return { type: 'image/jpeg' };
  }

  if (type === 'jpg') {
    return { type: 'image/jpg' };
  }

  if (type === 'png') {
    return { type: 'image/png' };
  }

  return { type: 'application/pdf' };
};

const getJourneyProductName = (productName) => {
  if (!productName) {
    return '-';
  }

  return PRODUCT_NAME[productName];
};

const numberWithCommas = (number) => {
  return number.toString().split('.')[0].length > 3
    ? number
        .toString()
        .substring(0, number.toString().split('.')[0].length - 3)
        .replace(/\B(?=(\d{2})+(?!\d))/g, ',') +
        ',' +
        number.toString().substring(number.toString().split('.')[0].length - 3)
    : number.toString();
};

export const formatDate = (date) => {
  let month = String(date.getMonth() + 1);
  let day = String(date.getDate());
  const year = String(date.getFullYear());

  if (month.length < 2) month = '0' + month;
  if (day.length < 2) day = '0' + day;

  return `${day}/${month}/${year}`;
};

const clearSessionData = () => {
  removeFromSessionStorage(SESSION_STORAGE.MODULE_ID, SESSION_STORAGE.PNQ_AUTH_TOKEN);
  removeFromSessionStorage(SESSION_STORAGE.MODULE_ID, SESSION_STORAGE.KYC_AUTH_TOKEN);
};

const isValidReqObj = (orderRequest) => {
  let isValid = true;

  if (orderRequest['purchaseOrderAmount'] === '' || orderRequest['purchaseOrderDate'] === '' || orderRequest['purchaseOrderNumber'] === 'NA') {
    isValid = false;
  }

  return isValid;
};

export const getContactsDetails = ({ contacts }) => {
  if (Array.isArray(contacts) && contacts.length > 0) {
    const { contactId, contactType } = contacts[0];
    return { contactId, contactType };
  }
  return {};
};

const setBillingAddress = (updateOrderSchema, billingForm) => {
  updateOrderSchema['gstNumber'] = billingForm?.gstin?.value ?? 'NA';
  updateOrderSchema['billingAddress']['addressLine1'] = billingForm.address.value || '';
  updateOrderSchema['billingAddress']['city'] = billingForm.city.value.key || '';
  updateOrderSchema['billingAddress']['pinCode'] = billingForm.pinCode.value || '';
  updateOrderSchema['billingContactDetails'] = {
    email: billingForm.email.value || '',
    phoneNumber: billingForm.mobileNumber.value || '',
    firstName: billingForm.personName.value || ''
  };

  return updateOrderSchema;
};

const setShippingAddress = (updateOrderSchema, form) => {
  updateOrderSchema['shippingAddress']['addressLine1'] = form.address.value || '';
  updateOrderSchema['shippingAddress']['city'] = form.city.value.key || '';
  updateOrderSchema['shippingAddress']['pinCode'] = form.pinCode.value || '';

  return updateOrderSchema;
};

export const getRequestForBillingandShippingUpdate = (form, orderDetail) => {
  const {
    lead: { connectivityLineItems, customer }
  } = orderDetail;

  const officeNameAddress = customer?.authorizedSignatory?.companyName ?? connectivityLineItems?.[0]?.companyAddress?.officeName ?? '';

  const updateOrderSchema = {
    billingAddress: {
      addressLine1: '',
      city: '',
      pinCode: '',
      officeName: officeNameAddress ?? ''
    },
    gstNumber: '',
    panNumber: '',
    shippingAddress: {
      addressLine1: '',
      city: '',
      pinCode: '',
      officeName: officeNameAddress ?? ''
    },
    billingContactDetails: {
      email: '',
      phoneNumber: '',
      firstName: ''
    }
  };

  const customerPanNumber = customer?.panNumber || '';
  const encryptedPanNumber = btoa(customerPanNumber);

  updateOrderSchema['panNumber'] = encryptedPanNumber;

  setBillingAddress(updateOrderSchema, form.billingDetails);
  setShippingAddress(updateOrderSchema, form.shippingDetails);
  return { ...updateOrderSchema };
};

const getRequestForOrderUpdate = (form, orderDetail, isPurchaseForm = true, isPOChecked) => {
  let updateOrderSchema = {};
  const panNumber = orderDetail.lead.customer.panNumber || '';
  const encryptedPanNumber = btoa(panNumber);

  updateOrderSchema['panNumber'] = encryptedPanNumber;

  isPurchaseForm && setPurchaseDetail(updateOrderSchema, form.purchaseDetail, isPOChecked);
  return updateOrderSchema;
};

const setPurchaseDetail = (updateOrderSchema, purchaseDetail, isPOChecked) => {
  if (purchaseDetail) {
    updateOrderSchema['purchaseOrderAmount'] = isPOChecked ? purchaseDetail.purchaseOrderTotalCharges.value && parseInt(purchaseDetail.purchaseOrderTotalCharges.value) : '';
    updateOrderSchema['purchaseOrderDate'] = isPOChecked ? formatDate(purchaseDetail.purchaseOrderDate.value) : '';
    updateOrderSchema['purchaseOrderNumber'] = isPOChecked ? purchaseDetail.purchaseOrderNo.value : 'NA';
    updateOrderSchema['purchaseOrderExpiryDate'] = isPOChecked && purchaseDetail.purchaseOrderExpiryDate.value ? formatDate(purchaseDetail.purchaseOrderExpiryDate.value) : '';
  }
  return updateOrderSchema;
};

const monthsToYear = (months) => {
  return (months / 12).toFixed(1);
};

const yearToMonths = (year) => {
  return Math.floor(year * 12);
};

const formateDateToLocaleStringWithTime = (rawDate = new Date()) => {
  const date = new Date(rawDate);
  const dateWithMonth = date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short'
  });

  const timeWithTwelveHour = date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit'
  });

  return `${dateWithMonth}, ${date.getFullYear()}, ${timeWithTwelveHour}`;
};

const isTypeOfImage = ({ type }) => {
  const imageTypes = ['image/jpeg', 'image/jpg', 'image/png'];
  return imageTypes.includes(type);
};

function dataURItoBlob(dataURI) {
  // convert base64 to raw binary data held in a string
  // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
  let byteString = atob(dataURI.split(',')[1]);

  // separate out the mime component
  let mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

  // write the bytes of the string to an ArrayBuffer
  let ab = new ArrayBuffer(byteString.length);

  // create a view into the buffer
  let ia = new Uint8Array(ab);

  // set the bytes of the buffer to the correct values
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  // write the ArrayBuffer to a blob, and you're done
  let blob = new Blob([ab], { type: mimeString });
  return blob;
}

const prepareRequestForKAM = (leadData, { commentText: remarks, attachments, userType }) => {
  const reqObj = {
    attachments,
    contractPeriod: leadData && leadData.contractPeriod && leadData.contractPeriod.toString(),
    remarks,
    updatedPlans: [
      {
        cpqLineItemId: leadData && leadData.lineItems[0] && leadData.lineItems[0].cpqLineItemId,
        lineItemId: leadData && leadData.lineItems[0] && leadData.lineItems[0].lineItemId,
        price: {
          rental: leadData && leadData.lineItems[0] && leadData.lineItems[0].plan && leadData.lineItems[0].plan.price && leadData.lineItems[0].plan.price.rental,
          oneTimeCharge: leadData && leadData.lineItems[0] && leadData.lineItems[0].plan && leadData.lineItems[0].plan.price && leadData.lineItems[0].plan.price.oneTimeCharge
        }
      }
    ]
  };
  return reqObj;
};

function useInterval(callback, delay = CALLBACK_DELAY) {
  const callbackRef = useRef();

  useEffect(() => {
    callbackRef.current = callback;
  });

  useEffect(() => {
    function invokeCb() {
      callbackRef.current();
    }

    if (delay !== null) {
      let id = setInterval(invokeCb, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

const useComponentWillMount = (cb) => {
  const willMount = useRef(true);

  if (willMount.current) {
    cb();
  }

  willMount.current = false;
};

function useWindowSize() {
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined
  });

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    }

    window.addEventListener('resize', handleResize);

    // Calling handler right away so that state gets updated with initial window size
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
}

const openInNewTab = (url) => {
  const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
  if (newWindow) newWindow.opener = null;
};

const redirectToTMO = (toDashboard = true, orderId = '') => {
  const env = Environment.getEnv();
  const tmoBaseUrl = env['dataTmoTrackOrderBasePageUrl'];
  const redirectPage = toDashboard ? 'dashboard' : `confirmed/${orderId}/details`;

  const partyId = findParamFromUrl('partyId', false);

  let params = [];

  partyId && params.push(`partyId=${partyId}`);

  params = params.join('&');

  openInNewTab(`${tmoBaseUrl}${redirectPage}?${params}`);
};

const splittedBwValue = (bandwidthWithUnit) => {
  if (!bandwidthWithUnit) {
    return [];
  }

  const [bwValue, bwUnit] = bandwidthWithUnit && bandwidthWithUnit.split(' ');

  return [bwValue, bwUnit];
};

const isValidMedia = (type, feasibilityResponse, requestedBandwidth) => {
  const { feasibilityType, wirelineNetworkElement, offNetResponse, wirelessNetworkElement, thirdPartyResponse } = feasibilityResponse;

  // maxBandwidth keys for different media types - WIRELINE (maxspeed - 1000), OFFNET (offNetMaxSupportedBandwidth - 20 Mbps), WIRELESS (maxspeed - 4 Mbps), THIRD PARTY (maxSupportedBandwidth - 20 Mbps)

  let isValid = false;
  let selectedMediaMaxFeasibleBw = '';

  if (!feasibilityType || !Array.isArray(feasibilityType) || !feasibilityType.length || !feasibilityType.includes(type)) {
    return [isValid, selectedMediaMaxFeasibleBw];
  }

  switch (type) {
    case MEDIA_TYPES.WIRELINE: {
      const maxFeasibleBw = wirelineNetworkElement && wirelineNetworkElement.maxspeed;
      const isUnitComingFromApi = maxFeasibleBw && maxFeasibleBw.includes('Mbps');
      const [bwValue] = splittedBwValue(maxFeasibleBw);
      selectedMediaMaxFeasibleBw = bwValue;
      if (wirelineNetworkElement && Object.keys(wirelineNetworkElement).length > 0 && parseInt(requestedBandwidth) <= parseInt(isUnitComingFromApi ? bwValue : maxFeasibleBw)) {
        isValid = true;
      }

      break;
    }
    case MEDIA_TYPES.OFFNET: {
      const maxFeasibleBw = offNetResponse && offNetResponse.offNetMaxSupportedBandwidth;
      const isUnitComingFromApi = maxFeasibleBw && maxFeasibleBw.includes('Mbps');
      const [bwValue] = splittedBwValue(maxFeasibleBw);
      selectedMediaMaxFeasibleBw = bwValue;
      if (offNetResponse && Object.keys(offNetResponse).length > 0 && parseInt(requestedBandwidth) <= parseInt(isUnitComingFromApi ? bwValue : maxFeasibleBw)) {
        isValid = true;
      }

      break;
    }
    case MEDIA_TYPES.WIRELESS: {
      const maxFeasibleBw = wirelessNetworkElement && wirelessNetworkElement.maxspeed;
      const isUnitComingFromApi = maxFeasibleBw && maxFeasibleBw.includes('Mbps');
      const [bwValue] = splittedBwValue(maxFeasibleBw);
      selectedMediaMaxFeasibleBw = bwValue;
      if (wirelessNetworkElement && Object.keys(wirelessNetworkElement).length > 0 && parseInt(requestedBandwidth) <= parseInt(isUnitComingFromApi ? bwValue : maxFeasibleBw)) {
        isValid = true;
      }

      break;
    }
    case MEDIA_TYPES.THIRD_PARTY: {
      const maxFeasibleBw = thirdPartyResponse && thirdPartyResponse.maxSupportedBandwidth;
      const isUnitComingFromApi = maxFeasibleBw && maxFeasibleBw.includes('Mbps');
      const [bwValue] = splittedBwValue(maxFeasibleBw);
      selectedMediaMaxFeasibleBw = bwValue;
      if (thirdPartyResponse && Object.keys(thirdPartyResponse).length > 0 && parseInt(requestedBandwidth) <= parseInt(isUnitComingFromApi ? bwValue : maxFeasibleBw)) {
        isValid = true;
      }

      break;
    }
    default: {
      isValid = false;
    }
  }

  return [isValid, selectedMediaMaxFeasibleBw];
};

const setMediaOptions = (feasibilityResponse, requestedBandwidth) => {
  const primaryMediaList = [];

  if (!feasibilityResponse) {
    return { primaryMediaList };
  }

  const { wirelineNetworkElement, offNetResponse, wirelessNetworkElement, thirdPartyResponse } = feasibilityResponse;

  // push priority wise in the array - ['WIRELINE', 'Offnet', 'WIRELESS', 'Third Party']

  if (isValidMedia(MEDIA_TYPES.WIRELINE, feasibilityResponse, requestedBandwidth)[0]) {
    wirelineNetworkElement['id'] = wirelineNetworkElement.media;
    wirelineNetworkElement['key'] = wirelineNetworkElement.media;
    wirelineNetworkElement['displayName'] = wirelineNetworkElement.media;
    wirelineNetworkElement['maxFeasibleBw'] = isValidMedia(MEDIA_TYPES.WIRELINE, feasibilityResponse, requestedBandwidth)[1];

    primaryMediaList.push(wirelineNetworkElement);
  }

  if (isValidMedia(MEDIA_TYPES.OFFNET, feasibilityResponse, requestedBandwidth)[0]) {
    offNetResponse['id'] = MEDIA_TYPES.OFFNET;
    offNetResponse['key'] = MEDIA_TYPES.OFFNET;
    offNetResponse['displayName'] = MEDIA_TYPES.OFFNET;
    offNetResponse['maxFeasibleBw'] = isValidMedia(MEDIA_TYPES.OFFNET, feasibilityResponse, requestedBandwidth)[1];

    primaryMediaList.push(offNetResponse);
  }

  if (isValidMedia(MEDIA_TYPES.WIRELESS, feasibilityResponse, requestedBandwidth)[0]) {
    wirelessNetworkElement['id'] = wirelessNetworkElement.technology;
    wirelessNetworkElement['key'] = wirelessNetworkElement.technology;
    wirelessNetworkElement['displayName'] = wirelessNetworkElement.technology;
    wirelessNetworkElement['maxFeasibleBw'] = isValidMedia(MEDIA_TYPES.WIRELESS, feasibilityResponse, requestedBandwidth)[1];

    primaryMediaList.push(wirelessNetworkElement);
  }

  if (isValidMedia(MEDIA_TYPES.THIRD_PARTY, feasibilityResponse, requestedBandwidth)[0]) {
    thirdPartyResponse['id'] = MEDIA_TYPES.THIRD_PARTY;
    thirdPartyResponse['key'] = MEDIA_TYPES.THIRD_PARTY;
    thirdPartyResponse['displayName'] = MEDIA_TYPES.THIRD_PARTY;
    thirdPartyResponse['maxFeasibleBw'] = isValidMedia(MEDIA_TYPES.THIRD_PARTY, feasibilityResponse, requestedBandwidth)[1];

    primaryMediaList.push(thirdPartyResponse);
  }

  return { primaryMediaList };
};

const getRange = (start, end, step = 1) => {
  const range = [];
  const typeofStart = typeof start;
  const typeofEnd = typeof end;

  if (step === 0) {
    throw TypeError('Step cannot be zero.');
  }

  if (typeofStart == 'undefined' || typeofEnd == 'undefined') {
    throw TypeError('Must pass start and end arguments.');
  } else if (typeofStart != typeofEnd) {
    throw TypeError('Start and end arguments must be of same type.');
  }

  if (end < start) {
    step = -step;
  }

  if (typeofStart == 'number') {
    while (step > 0 ? end >= start : end <= start) {
      range.push(start);
      start += step;
    }
  } else if (typeofStart == 'string') {
    if (start.length != 1 || end.length != 1) {
      throw TypeError('Only strings with one character are supported.');
    }

    start = start.charCodeAt(0);
    end = end.charCodeAt(0);

    while (step > 0 ? end >= start : end <= start) {
      range.push(String.fromCharCode(start));
      start += step;
    }
  } else {
    throw TypeError('Only string and number types are supported');
  }

  return range;
};

const isValidObject = (data) => data && Object.keys(data).length > 0;

const isLeadInProgress = (leadData) => {
  return leadData.leadStatus === LEAD_STATUS.LEAD_IN_PROGRESS;
};

const hasValidSiteDetails = (leadData) => {
  return (
    leadData &&
    Array.isArray(leadData.lineItems) &&
    leadData.lineItems.length > 0 &&
    leadData.lineItems[0] &&
    leadData.lineItems[0].plan &&
    Array.isArray(leadData.lineItems[0].plan.site) &&
    leadData.lineItems[0].plan.site.length > 0
  );
};

const hasValidSiteSpecs = (leadData) => {
  return (
    hasValidSiteDetails(leadData) &&
    leadData.lineItems[0].plan.site[0] &&
    Array.isArray(leadData.lineItems[0].plan.site[0].siteSpecs) &&
    leadData.lineItems[0].plan.site[0].siteSpecs.length > 0
  );
};

const hasValidMediaAndSiteDetails = ({ selectedPrimaryMedia, leadDataCopy }) => {
  return isValidObject(selectedPrimaryMedia) && selectedPrimaryMedia.hasOwnProperty('key') && !isLeadInProgress(leadDataCopy) && hasValidSiteDetails(leadDataCopy);
};

const hasValidMediaAndSiteSpecs = ({ selectedPrimaryMedia, leadDataCopy }) => {
  return isValidObject(selectedPrimaryMedia) && hasValidSiteSpecs(leadDataCopy);
};

String.prototype.toTitleCase = function () {
  if (this) {
    return this.replace(/\w\S*/g, function (txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  } else {
    return this;
  }
};

export {
  validateFileAndGetFormData,
  isFormValid,
  getFileName,
  getFileType,
  numberWithCommas,
  numberValidator,
  alphaNumbericValidator,
  alphaNumericWithSpecialCharactersValidator,
  formElement,
  getDate,
  getRequestForOrderUpdate,
  mobileValidator,
  emailValidator,
  getJourneyProductName,
  monthsToYear,
  yearToMonths,
  formateDateToLocaleStringWithTime,
  isTypeOfImage,
  dataURItoBlob,
  prepareRequestForKAM,
  useInterval,
  redirectToTMO,
  useComponentWillMount,
  useWindowSize,
  isValidReqObj,
  clearSessionData,
  splittedBwValue,
  setMediaOptions,
  getRange,
  isValidObject,
  hasValidMediaAndSiteDetails,
  hasValidMediaAndSiteSpecs
};
